import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { screensApi } from '../screens/Screens.api';
import { OnDemandQueue } from './OnDemandQueue';
import { ON_DEMAND_PATH } from './OnDemand.types';
import { OnDemandWizard } from './OnDemandWizard';

/** Beam on Screen → one screen's On Demand Player (`/#/beamonscreen/ods/:screenId`). */
export const OnDemandScreenPage: React.FC = () => {
    const { screenId = '' } = useParams();
    const [screenName, setScreenName] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        screensApi
            .get(screenId)
            .then((screen) => {
                if (!cancelled) {
                    setScreenName(screen.name);
                    setError(null);
                }
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Screen not found');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [screenId]);

    return (
        <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} pr={{ xs: 1.25, md: 2.5 }} width="100%" mb={3}>
            <DynamicBreadcrumbs
                items={[
                    { itemPath: '/dashboard', itemLabel: 'Home' },
                    { itemPath: ON_DEMAND_PATH, itemLabel: 'On Demand Player' },
                    { itemPath: `${ON_DEMAND_PATH}/${screenId}`, itemLabel: screenName || 'Screen' },
                ]}
            />
            {loading && (
                <Box display="flex" justifyContent="center" py={6}><CircularProgress color="secondary" /></Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}
            {!loading && !error && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>{screenName}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                        <OnDemandWizard screenId={screenId} onScheduled={() => setRefreshToken((value) => value + 1)} />
                        <OnDemandQueue screenId={screenId} refreshToken={refreshToken} />
                    </Box>
                </>
            )}
        </Box>
    );
};
